import { useCallback, useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import TrialBanner from "@/components/TrialBanner";
import api from "@/lib/api";
import { downloadRoadmapPDF } from "@/lib/pdfExport";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeGate from "@/components/UpgradeGate";
import { hasPlan } from "@/lib/planAccess";
import {
  ArrowsClockwise,
  Flame,
  Star,
  TrendUp,
  Clock,
  Target,
  GraduationCap,
  CalendarBlank,
  Trophy,
  Sparkle,
  DownloadSimple,
  ChartLineUp,
  CheckCircle,
  Circle,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const COUNTRY_CURRENCY = {
  india: { code: "INR", symbol: "₹" },
  in: { code: "INR", symbol: "₹" },
  "united arab emirates": { code: "AED", symbol: "AED" },
  uae: { code: "AED", symbol: "AED" },
  ae: { code: "AED", symbol: "AED" },
  "saudi arabia": { code: "SAR", symbol: "SAR" },
  ksa: { code: "SAR", symbol: "SAR" },
  saudi: { code: "SAR", symbol: "SAR" },
  qatar: { code: "QAR", symbol: "QAR" },
  kuwait: { code: "KWD", symbol: "KWD" },
  bahrain: { code: "BHD", symbol: "BHD" },
  oman: { code: "OMR", symbol: "OMR" },
  "united states": { code: "USD", symbol: "$" },
  usa: { code: "USD", symbol: "$" },
  us: { code: "USD", symbol: "$" },
  "united kingdom": { code: "GBP", symbol: "£" },
  uk: { code: "GBP", symbol: "£" },
  canada: { code: "CAD", symbol: "C$" },
  australia: { code: "AUD", symbol: "A$" },
  singapore: { code: "SGD", symbol: "S$" },
  germany: { code: "EUR", symbol: "€" },
  france: { code: "EUR", symbol: "€" },
  netherlands: { code: "EUR", symbol: "€" },
  ireland: { code: "EUR", symbol: "€" },
  "new zealand": { code: "NZD", symbol: "NZ$" },
  "south africa": { code: "ZAR", symbol: "R" },
  malaysia: { code: "MYR", symbol: "RM" },
  japan: { code: "JPY", symbol: "¥" },
};

function getDashboardCurrency(profile, roadmap) {
  const storedSymbol =
    profile?.currency_symbol || roadmap?.currency_symbol;

  const countryKey = String(profile?.country || "")
    .trim()
    .toLowerCase();

  const mapped = COUNTRY_CURRENCY[countryKey];

  if (mapped) {
    return {
      ...mapped,
      symbol: mapped.symbol,
    };
  }

  return {
    code: profile?.currency || roadmap?.currency || "USD",
    symbol:
      storedSymbol || (profile?.currency === "USD" ? "$" : ""),
  };
}

export default function Dashboard() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");

  const [rm, setRm] = useState(null);
  const [progress, setProgress] = useState({
    completed: [],
    count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [roadmapUsage, setRoadmapUsage] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [savingProgress, setSavingProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      const [roadmapRes, progressRes, usageRes] = await Promise.all([
        api.get("/roadmap"),
        api.get("/learning/progress"),
        api.get("/billing/usage"),
      ]);

      setRm(roadmapRes.data);
      setRoadmapUsage(usageRes.data || null);
      setProgress(
        progressRes.data || {
          completed: [],
          count: 0,
        }
      );
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("dashboard load failed", err);
      }

      if (err.config?.url?.includes("/roadmap")) {
        setRm(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const regenerate = useCallback(async () => {
    if (!paid) {
      toast.error("Roadmap refresh is available on Pro and Premium.");
      return;
    }

    const limit = Number(roadmapUsage?.limits?.roadmap_regenerations ?? 0);
    const used = Number(roadmapUsage?.used?.roadmap_regenerations ?? 0);

    if (limit > 0 && used >= limit) {
      toast.error(
        user?.tier === "pro"
          ? "You have used your 1 Pro roadmap refresh this month."
          : "You have used your 2 Premium roadmap refreshes this month."
      );
      return;
    }

    setRegenerating(true);

    try {
      const { data } = await api.post("/roadmap/regenerate");
      setRm(data);

      const { data: freshUsage } = await api.get("/billing/usage");
      setRoadmapUsage(freshUsage || null);

      toast.success("Roadmap refreshed");
    } catch (err) {
      console.error("regenerate failed", err);
      const detail = err.response?.data?.detail || "Failed to regenerate";
      toast.error(detail);
      if (err.response?.status === 403) {
        const { data: freshUsage } = await api.get("/billing/usage").catch(() => ({ data: null }));
        if (freshUsage) setRoadmapUsage(freshUsage);
      }
    } finally {
      setRegenerating(false);
    }
  }, [paid, roadmapUsage, user?.tier]);

  const downloadPDF = useCallback(() => {
    if (!rm || rm.error) {
      toast.error("No roadmap to export yet");
      return;
    }

    try {
      downloadRoadmapPDF(rm, user);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("pdf export failed", err);
      toast.error("Export failed");
    }
  }, [rm, user]);

  const roadmapPhases = rm?.learning_roadmap || [];

  const completedIds = new Set(
    (progress.completed || []).map(
      (item) => item.lesson_id
    )
  );

  const completedPhases = roadmapPhases.filter(
    (_, i) =>
      completedIds.has(`roadmap-phase-${i}`)
  ).length;

  const overallProgress = roadmapPhases.length
    ? Math.round(
        (completedPhases / roadmapPhases.length) * 100
      )
    : 0;

  const currentPhaseIndex = roadmapPhases.findIndex(
    (_, i) =>
      !completedIds.has(`roadmap-phase-${i}`)
  );

  const togglePhase = async (index) => {
    const phase = roadmapPhases[index];
    const lessonId = `roadmap-phase-${index}`;

    if (
      !phase ||
      completedIds.has(lessonId) ||
      savingProgress
    ) {
      return;
    }

    setSavingProgress(lessonId);

    try {
      await api.post("/learning/complete", {
        lesson_id: lessonId,
        title: `Roadmap Phase ${index + 1}: ${phase.phase}`,
      });

      const { data } = await api.get(
        "/learning/progress"
      );

      setProgress(
        data || {
          completed: [],
          count: 0,
        }
      );

      toast.success(
        `Phase ${index + 1} completed`
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Could not save progress"
      );
    } finally {
      setSavingProgress(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505]"
      data-testid="dashboard-page"
    >
      <Navbar />
      <DashSidebar />

      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-[1400px] mx-auto lg:mx-0">
        <TrialBanner />

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">
              // Mission Control
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight font-medium">
              Hello,{" "}
              {user?.name?.split(" ")[0] || "there"}.
            </h1>

            <p className="text-zinc-500 mt-2 text-sm sm:text-base">
              Your roadmap to{" "}
              <span className="text-white">
                {user?.profile?.desired_career}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <StatPill
              icon={Flame}
              label="Streak"
              value={`${user?.streak || 0}d`}
              color="text-orange-400"
              testId="stat-streak"
            />

            <StatPill
              icon={Star}
              label="XP"
              value={user?.xp || 0}
              color="text-blue-400"
              testId="stat-xp"
            />

            <StatPill
              icon={Trophy}
              label="Level"
              value={user?.level || 1}
              color="text-amber-400"
              testId="stat-level"
            />

            {paid && (() => {
              const limit = Number(roadmapUsage?.limits?.roadmap_regenerations ?? 0);
              const used = Number(roadmapUsage?.used?.roadmap_regenerations ?? 0);
              const exhausted = limit > 0 && used >= limit;
              const label = regenerating
                ? "Regenerating..."
                : exhausted
                  ? "Refresh limit reached"
                  : `Refresh (${used}/${limit})`;

              return (
                <button
                  onClick={regenerate}
                  disabled={regenerating || exhausted}
                  data-testid="regenerate-btn"
                  title={
                    exhausted
                      ? `You have used all ${limit} roadmap refresh${limit === 1 ? "" : "es"} for this month.`
                      : `You can refresh your roadmap ${Math.max(0, limit - used)} more time${limit - used === 1 ? "" : "s"} this month.`
                  }
                  className="bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed border border-white/10 text-white px-3 sm:px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 transition-colors min-h-[40px]"
                >
                  <ArrowsClockwise
                    size={14}
                    className={regenerating ? "animate-spin" : ""}
                  />
                  {label}
                </button>
              );
            })()}

            <button
              onClick={downloadPDF}
              disabled={!rm || rm?.error}
              data-testid="download-pdf-btn"
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 sm:px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 transition-colors min-h-[40px]"
            >
              <DownloadSimple
                size={14}
                weight="bold"
              />

              <span className="hidden sm:inline">
                Download{" "}
              </span>
              PDF
            </button>
          </div>
        </div>

        {loading && (
          <div
            className="text-zinc-500"
            data-testid="loading-roadmap"
          >
            Loading your roadmap...
          </div>
        )}

        {!loading && rm && rm.error && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-amber-200">
            We couldn&apos;t fully parse the AI response.{" "}
            <button
              className="underline"
              onClick={regenerate}
            >
              Try regenerating
            </button>
            .
          </div>
        )}

        {!loading && rm && !rm.error && (
          <>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 items-start min-w-0"
              data-testid="roadmap-grid"
            >
              {/* LEFT COLUMN */}
              <div className="min-w-0 flex flex-col gap-4">
                <Card
                  className="self-start"
                  testId="card-gap"
                >
                  <CardHeader
                    icon={Target}
                    title="Career Gap Analysis"
                    accent="blue"
                  />

                  <p className="text-zinc-300 leading-relaxed mt-3">
                    {rm.career_gap_analysis}
                  </p>
                </Card>

                <Card
                  className="self-start"
                  testId="card-skills"
                >
                  <CardHeader
                    icon={Sparkle}
                    title="Skill Gap Analysis"
                    accent="blue"
                  />

                  <div className="mt-4 space-y-2">
                    {(rm.skill_gaps || []).map(
                      (s, i) => (
                        <div
                          key={`${i}-${s.skill}`}
                          className="flex items-center justify-between bg-white/[0.02] border border-white/5 rounded-lg px-4 py-3"
                        >
                          <div>
                            <div className="text-sm font-medium">
                              {s.skill}
                            </div>

                            <div className="text-xs text-zinc-500">
                              {s.current_level} →{" "}
                              {s.target_level}
                            </div>
                          </div>

                          <span
                            className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
                              s.priority === "High"
                                ? "bg-red-500/15 text-red-400"
                                : s.priority === "Medium"
                                ? "bg-amber-500/15 text-amber-400"
                                : "bg-zinc-500/15 text-zinc-400"
                            }`}
                          >
                            {s.priority}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </Card>
              </div>

              {/* RIGHT COLUMN */}
              <div className="min-w-0 flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card
                    className="min-w-0"
                    testId="card-time"
                  >
                    <CardHeader
                      icon={Clock}
                      title="Time to Goal"
                      accent="blue"
                    />

                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="text-4xl font-medium tracking-tight">
                        {rm.estimated_months_to_goal}
                      </span>

                      <span className="text-zinc-500">
                        months
                      </span>
                    </div>

                    <p className="text-xs text-zinc-500 mt-1">
                      Based on your study hours
                    </p>
                  </Card>

                  <Card
                    className="min-w-0"
                    testId="card-salary"
                  >
                    <CardHeader
                      icon={TrendUp}
                      title="Salary Trajectory"
                      accent="green"
                    />

                    <div className="mt-3 space-y-1.5 text-sm">
                      {(() => {
                        const salaryCurrency =
                          getDashboardCurrency(
                            user?.profile,
                            rm
                          );

                        return (
                          rm.salary_estimate &&
                          Object.entries(
                            rm.salary_estimate
                          ).map(([k, v]) => (
                            <div
                              key={k}
                              className="flex justify-between"
                            >
                              <span className="text-zinc-500 capitalize">
                                {k}
                              </span>

                              <span className="font-mono text-emerald-400">
                                {salaryCurrency.symbol}

                                {salaryCurrency.symbol
                                  ? ""
                                  : salaryCurrency.code +
                                    " "}

                                {typeof v === "number"
                                  ? v.toLocaleString()
                                  : v}
                              </span>
                            </div>
                          ))
                        );
                      })()}
                    </div>
                  </Card>
                </div>

                <Card
                  className="self-start"
                  testId="card-weekly"
                >
                  <CardHeader
                    icon={CalendarBlank}
                    title="Weekly Study Plan"
                    accent="blue"
                  />

                  <div className="mt-3 grid grid-cols-7 gap-1 overflow-x-auto">
                    {(rm.weekly_study_plan || []).map(
                      (d, i) => {
                        const isSunday = String(
                          d.day || ""
                        )
                          .trim()
                          .toLowerCase()
                          .startsWith("sun");

                        return (
                          <div
                            key={`${i}-${d.day}`}
                            className={`text-center border rounded-lg px-1.5 py-3 min-w-0 ${
                              isSunday
                                ? "border-amber-500/15 bg-amber-500/[0.03]"
                                : "border-white/5 bg-white/[0.02]"
                            }`}
                          >
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500 truncate">
                              {d.day}
                            </div>

                            {isSunday ? (
                              <>
                                <div className="text-sm font-medium text-amber-400 mt-1">
                                  REST
                                </div>

                                <div className="text-[9px] text-zinc-500 truncate">
                                  Recovery day
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-sm font-medium font-mono text-blue-400 mt-1">
                                  {d.hours}h
                                </div>

                                <div
                                  className="text-[9px] text-zinc-500 truncate"
                                  title={d.focus}
                                >
                                  {d.focus}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                </Card>

                {/* CAREER PROGRESS */}
                <Card
                  className="self-start min-w-0 w-full"
                  testId="card-progress"
                >
                  {!paid ? (
                    <UpgradeGate
                      compact
                      title="Career Progress is a Pro feature"
                      description="Unlock the progress bar and detailed roadmap tracking."
                    />
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg border border-blue-500/20 bg-blue-500/10 flex items-center justify-center shrink-0">
                            <ChartLineUp
                              size={18}
                              weight="duotone"
                              className="text-blue-400"
                            />
                          </div>

                          <div className="min-w-0">
                            <h3 className="text-base font-medium">
                              Career Progress
                            </h3>

                            <p className="text-[11px] text-zinc-500 mt-0.5">
                              Your roadmap at a glance
                            </p>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <div className="text-xl font-medium font-mono text-blue-400">
                            {overallProgress}%
                          </div>

                          <div className="text-[9px] uppercase tracking-widest text-zinc-600">
                            complete
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all duration-500"
                          style={{
                            width: `${overallProgress}%`,
                          }}
                        />
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
                          <div className="text-[9px] uppercase tracking-widest text-zinc-600">
                            Phases
                          </div>

                          <div className="text-sm font-medium mt-0.5">
                            {completedPhases}

                            <span className="text-zinc-600">
                              /{roadmapPhases.length}
                            </span>
                          </div>
                        </div>

                        <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
                          <div className="text-[9px] uppercase tracking-widest text-zinc-600">
                            Activities
                          </div>

                          <div className="text-sm font-medium mt-0.5">
                            {progress.count || 0}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5">
                        <div className="text-[9px] uppercase tracking-widest text-zinc-600">
                          Next milestone
                        </div>

                        {currentPhaseIndex >= 0 ? (
                          <>
                            <div className="text-xs font-medium text-white mt-1 truncate">
                              Phase{" "}
                              {currentPhaseIndex + 1} ·{" "}
                              {
                                roadmapPhases[
                                  currentPhaseIndex
                                ]?.phase
                              }
                            </div>

                            <div className="text-[10px] text-zinc-500 mt-0.5 line-clamp-2 break-words">
                              {roadmapPhases[
                                currentPhaseIndex
                              ]?.milestone ||
                                "Continue with your current roadmap phase."}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-xs font-medium text-emerald-400 mt-1">
                              Roadmap complete 🎉
                            </div>

                            <div className="text-[10px] text-zinc-500 mt-0.5">
                              Keep building skills and job-ready proof.
                            </div>
                          </>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setTracking(
                            (value) => !value
                          )
                        }
                        data-testid="track-progress-btn"
                        className="mt-3 w-full rounded-lg border border-blue-500/25 bg-blue-500/[0.06] hover:bg-blue-500/[0.11] hover:border-blue-500/40 px-3 py-2.5 text-xs font-medium text-blue-300 transition-colors inline-flex items-center justify-center gap-2"
                      >
                        <ChartLineUp size={15} />

                        {tracking
                          ? "Hide detailed progress"
                          : "Track Progress"}

                        <CaretRight
                          size={13}
                          className={`transition-transform ${
                            tracking
                              ? "rotate-90"
                              : ""
                          }`}
                        />
                      </button>
                    </>
                  )}
                </Card>

                {tracking && (
                  <Card
                    className="self-start min-w-0 w-full"
                    testId="card-progress-details"
                  >
                    <div
                      id="progress-tracker"
                      className="min-w-0 overflow-hidden"
                    >
                      <div className="text-[9px] uppercase tracking-widest text-zinc-600 mb-2">
                        Roadmap stages
                      </div>

                      <div className="space-y-1.5">
                        {roadmapPhases.map(
                          (phase, i) => {
                            const lessonId = `roadmap-phase-${i}`;
                            const done =
                              completedIds.has(
                                lessonId
                              );
                            const active =
                              i === currentPhaseIndex;

                            return (
                              <button
                                key={lessonId}
                                type="button"
                                disabled={
                                  done ||
                                  savingProgress ===
                                    lessonId
                                }
                                onClick={() =>
                                  togglePhase(i)
                                }
                                className={`w-full min-w-0 text-left flex items-center gap-2 rounded-lg border px-2.5 py-2 transition-colors ${
                                  done
                                    ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                                    : active
                                    ? "border-blue-500/30 bg-blue-500/[0.06] hover:bg-blue-500/[0.1]"
                                    : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04]"
                                }`}
                              >
                                {done ? (
                                  <CheckCircle
                                    size={16}
                                    weight="fill"
                                    className="text-emerald-400 shrink-0"
                                  />
                                ) : (
                                  <Circle
                                    size={16}
                                    className={
                                      active
                                        ? "text-blue-400 shrink-0"
                                        : "text-zinc-600 shrink-0"
                                    }
                                  />
                                )}

                                <div className="min-w-0 flex-1">
                                  <div
                                    className={`text-[11px] truncate ${
                                      done
                                        ? "text-zinc-500 line-through"
                                        : "text-zinc-200"
                                    }`}
                                  >
                                    {phase.phase}
                                  </div>

                                  <div className="text-[9px] text-zinc-600">
                                    Phase {i + 1} ·{" "}
                                    {phase.duration_weeks}w
                                    {active && !done
                                      ? " · NEXT"
                                      : ""}
                                  </div>
                                </div>

                                {!done && (
                                  <CaretRight
                                    size={12}
                                    className="text-zinc-600 shrink-0"
                                  />
                                )}
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            </div>

            {/* Learning Roadmap */}
            <Card
              className="mt-4"
              testId="card-roadmap"
            >
              <CardHeader
                icon={GraduationCap}
                title="Learning Roadmap"
                accent="blue"
              />

              {!paid ? (
                <UpgradeGate
                  compact
                  title="Learning Roadmap is locked"
                  description="Upgrade to Pro or Premium to unlock your phase-by-phase learning roadmap."
                />
              ) : (
                <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {(rm.learning_roadmap || []).map(
                    (p, i) => (
                      <div
                        key={`${i}-${p.phase}`}
                        className="bg-white/[0.02] border border-white/5 rounded-lg p-4"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
                          Phase {i + 1} ·{" "}
                          {p.duration_weeks}w
                        </div>

                        <div className="text-sm font-medium mt-1">
                          {p.phase}
                        </div>

                        <ul className="mt-2 space-y-1 text-xs text-zinc-400">
                          {(p.topics || [])
                            .slice(0, 4)
                            .map((t) => (
                              <li key={t}>
                                · {t}
                              </li>
                            ))}
                        </ul>

                        <div className="mt-3 text-xs text-emerald-400">
                          🎯 {p.milestone}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </Card>

            {/* Monthly Milestones */}
            <Card
              className="mt-4"
              testId="card-milestones"
            >
              <CardHeader
                icon={Trophy}
                title="Monthly Milestones"
                accent="amber"
              />

              {!paid ? (
                <UpgradeGate
                  compact
                  title="Monthly Milestones are locked"
                  description="Upgrade to Pro or Premium to unlock monthly career milestones."
                />
              ) : (
                <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                  {(rm.monthly_milestones || []).map(
                    (m, i) => (
                      <div
                        key={`${i}-${m.month}`}
                        className="min-w-[200px] bg-white/[0.02] border border-white/5 rounded-lg p-4"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">
                          Month {m.month}
                        </div>

                        <div className="text-sm text-zinc-200 mt-2 leading-snug">
                          {m.milestone}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

const StatPill = ({
  icon: Icon,
  label,
  value,
  color,
  testId,
}) => (
  <div
    className="bg-[#0F0F12] border border-white/10 rounded-lg px-3 py-2 flex items-center gap-2"
    data-testid={testId}
  >
    <Icon
      size={16}
      weight="fill"
      className={color}
    />

    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>

      <div className="text-sm font-medium font-mono">
        {value}
      </div>
    </div>
  </div>
);

const Card = ({
  className = "",
  children,
  testId,
}) => (
  <div
    data-testid={testId}
    className={`bg-[#0F0F12] border border-white/10 rounded-xl p-4 sm:p-5 hover:border-white/20 transition-colors ${className}`}
  >
    {children}
  </div>
);

const CardHeader = ({
  icon: Icon,
  title,
  accent = "blue",
}) => {
  const colors = {
    blue: "text-blue-400",
    green: "text-emerald-400",
    amber: "text-amber-400",
  };

  return (
    <div className="flex items-center gap-2">
      <Icon
        size={18}
        weight="duotone"
        className={colors[accent]}
      />

      <h3 className="text-lg font-medium">
        {title}
      </h3>
    </div>
  );
};
